import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface NewsArticle {
  id: string;
  title: string;
  summary: string | null;
  url: string;
  source: string | null;
  published_at: string;
  relevance_score: number | null;
  relevance_reason: string | null;
  sentiment: string | null;
  categories: string[] | null;
  related_companies: string[] | null;
  is_read: boolean;
  is_saved: boolean;
  is_dismissed: boolean;
  created_at: string;
}

export function useNewsFeed() {
  const { user } = useAuth();
  
  const { data: articles, isLoading, refetch } = useQuery({
    queryKey: ['news-feed', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('news_articles')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_dismissed', false)
        .order('relevance_score', { ascending: false })
        .order('published_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      return data as NewsArticle[];
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
  
  const markAsRead = async (articleId: string) => {
    const { error } = await supabase
      .from('news_articles')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', articleId);
    
    if (!error) {
      refetch();
    }
  };
  
  const saveArticle = async (articleId: string) => {
    const { error } = await supabase
      .from('news_articles')
      .update({ is_saved: true })
      .eq('id', articleId);
    
    if (!error) {
      toast.success('Article saved');
      refetch();
    }
  };
  
  const dismissArticle = async (articleId: string) => {
    const { error } = await supabase
      .from('news_articles')
      .update({ is_dismissed: true })
      .eq('id', articleId);
    
    if (!error) {
      toast.success('Article dismissed');
      refetch();
    }
  };
  
  const refreshFeed = async () => {
    try {
      toast.info('Fetching latest news...', { duration: 2000 });
      
      const { data, error } = await supabase.functions.invoke('fetch-news-feed', {
        body: {},
      });
      
      if (error) {
        console.error('Function invoke error:', error);
        throw error;
      }
      
      console.log('Refresh response:', data);
      toast.success(`Found ${data?.articlesFound || 0} articles. Analysis in progress...`);
      
      // Refetch after a short delay to get the processed articles
      setTimeout(() => refetch(), 3000);
      
      return data;
    } catch (error) {
      console.error('Error refreshing feed:', error);
      toast.error('Failed to refresh news feed. Check console for details.');
      throw error;
    }
  };
  
  return {
    articles: articles || [],
    isLoading,
    markAsRead,
    saveArticle,
    dismissArticle,
    refreshFeed,
    unreadCount: articles?.filter(a => !a.is_read).length || 0
  };
}
